/**
 * Thin fetch wrapper shared by the client and admin apps.
 * Base URL comes from each app's VITE_API_URL (see .env.example).
 */

import type { ApiFailure } from '../types/api';

export class ApiError extends Error {
  /** The server's machine-readable reason, e.g. `RATE_LIMITED`. */
  readonly code: string | undefined;

  constructor(
    message: string,
    readonly status: number,
    /** The server's `{ message, code, details }`, or null when the body was not JSON. */
    readonly body: ApiFailure | null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = body?.code;
  }
}

/**
 * TanStack Query's `retry` for both apps: try a failed read once more only
 * when a second try could go differently.
 *
 * - No response at all (offline, server restarting, a dropped connection) and
 *   5xx are transient — worth one more attempt.
 * - Any 4xx is the server's considered answer: a 404 stays missing, a 400 stays
 *   invalid, and a 429 retried at once only hits the same limit. Retrying them
 *   just delays the page that explains the problem.
 */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  return !(error instanceof ApiError) || error.status >= 500;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Called before each request — return headers to merge in (e.g. an auth token). */
  getHeaders?: () => Record<string, string> | Promise<Record<string, string>>;
  /** Send cookies with cross-origin requests. */
  credentials?: RequestCredentials;
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
}

/** A file the server streamed back, with the name it asked for. */
export interface BlobResponse {
  blob: Blob;
  /** From Content-Disposition, or null when the server did not name it. */
  filename: string | null;
  headers: Headers;
}

/**
 * The filename out of a Content-Disposition header.
 *
 * `filename*` wins when both are present: it is the encoded form, and it is
 * the one that survives a non-ASCII name.
 */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;

  const encoded = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // A malformed escape is not worth failing a download over.
    }
  }

  const quoted = /filename="([^"]*)"/.exec(header) ?? /filename=([^;]+)/.exec(header);
  return quoted ? quoted[1].trim() : null;
}

export function createApiClient({
  baseUrl,
  getHeaders,
  credentials = 'include',
}: ApiClientOptions) {
  // `new URL(path, base)` rejects a relative base, but both apps are configured with one
  // (`VITE_API_URL=/api`) so the dev proxy can serve the API from the page's own origin.
  // Resolve it against that origin up front; an absolute base passes through untouched.
  const withSlash = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const resolvedBase = /^[a-z][a-z0-9+.-]*:/i.test(withSlash)
    ? withSlash
    : new URL(withSlash, globalThis.location?.origin ?? 'http://localhost').toString();

  async function request<T>(
    method: string,
    path: string,
    { query, body, headers, ...init }: RequestOptions = {},
  ): Promise<T> {
    const url = new URL(path.replace(/^\//, ''), resolvedBase);

    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }

    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const res = await fetch(url, {
      ...init,
      method,
      credentials,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(await getHeaders?.()),
        ...headers,
      },
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
    });

    const payload = res.status === 204 ? null : await res.json().catch(() => null);

    if (!res.ok) {
      const message =
        (payload as { message?: string } | null)?.message ?? `Request failed (${res.status})`;
      throw new ApiError(message, res.status, payload);
    }

    return payload as T;
  }

  /**
   * A file response rather than a JSON one — report exports, and anything else
   * the server streams back as an attachment.
   *
   * It cannot go through `request`, which always parses the body as JSON. The
   * error path still can: a failure comes back as the usual `{ message, code }`
   * envelope, so it is read as JSON and thrown as an ApiError like any other.
   */
  async function requestBlob(
    method: string,
    path: string,
    { query, body, headers, ...init }: RequestOptions = {},
  ): Promise<BlobResponse> {
    const url = new URL(path.replace(/^\//, ''), resolvedBase);

    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }

    const res = await fetch(url, {
      ...init,
      method,
      credentials,
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(await getHeaders?.()),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      const message =
        (payload as { message?: string } | null)?.message ?? `Request failed (${res.status})`;
      throw new ApiError(message, res.status, payload);
    }

    return {
      blob: await res.blob(),
      filename: filenameFromDisposition(res.headers.get('Content-Disposition')),
      headers: res.headers,
    };
  }

  return {
    request,
    requestBlob,
    getBlob: (path: string, options?: RequestOptions) => requestBlob('GET', path, options),
    get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('POST', path, { ...options, body }),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('PUT', path, { ...options, body }),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('PATCH', path, { ...options, body }),
    delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
